import { useCallback, useRef } from 'react';

import { useNavigate } from 'react-router-dom';

import { useMutation, useQueryClient } from '@tanstack/react-query';
import { notification } from 'antd';

import { NOTIFICATION_MESSAGES, ROUTES } from '@/shared/lib/constants';
import { getParentFqn } from '@/shared/lib/format/fqn';
import { assetKeys, discoveryKeys, glossaryKeys } from '@/shared/lib/query/queryKeys';

import { deleteGlossaryTermApi } from '@/features/glossary/api';
import type { DeleteGlossaryTermRequest } from '@/features/glossary/api';

type DeleteTermContext = {
  termFqn?: string;
  termCategory?: string;
};

type UseDeleteGlossaryTermOptions = {
  onTreeRefresh?: (parentFqn: string) => void;
};

export const useDeleteGlossaryTerm = (
  termFqn?: string,
  termCategory?: string,
  options?: UseDeleteGlossaryTermOptions
) => {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const optionsRef = useRef(options);
  optionsRef.current = options;

  const mutation = useMutation({
    mutationFn: (request: DeleteGlossaryTermRequest) => deleteGlossaryTermApi(request),
    onMutate: async (): Promise<DeleteTermContext> => {
      if (termFqn) {
        await queryClient.cancelQueries({ queryKey: glossaryKeys.termDetailByFqn(termFqn) });
        queryClient.removeQueries({ queryKey: glossaryKeys.termDetailByFqn(termFqn) });
      }
      queryClient.removeQueries({ queryKey: glossaryKeys.termsPrefix() });

      return { termFqn, termCategory };
    },
    onSuccess: (_data, _variables, context) => {
      queryClient.invalidateQueries({ queryKey: glossaryKeys.termsAll() });
      queryClient.invalidateQueries({ queryKey: glossaryKeys.glossaryTree });

      queryClient.invalidateQueries({ queryKey: assetKeys.assetBaseInfoPrefix() });
      queryClient.invalidateQueries({ queryKey: assetKeys.dataAssetsPrefix() });
      queryClient.invalidateQueries({ queryKey: assetKeys.schemaPrefix() });
      queryClient.invalidateQueries({ queryKey: discoveryKeys.searchPrefix() });
      queryClient.invalidateQueries({ queryKey: assetKeys.lineagePrefix() });

      if (context?.termCategory) {
        navigate(`${ROUTES.GLOSSARY}/${context.termCategory}`, { replace: true });
      } else {
        navigate(ROUTES.GLOSSARY, { replace: true });
      }

      let parentFqn = context?.termCategory || '';

      if (context?.termFqn) {
        parentFqn = getParentFqn(context.termFqn, true);
        queryClient.invalidateQueries({ queryKey: glossaryKeys.childTerms(parentFqn) });
        queryClient.invalidateQueries({ queryKey: glossaryKeys.termDetailByFqnPrefix() });
      }

      optionsRef.current?.onTreeRefresh?.(parentFqn);

      setTimeout(() => {
        queryClient.invalidateQueries({ queryKey: glossaryKeys.all });
      }, 100);

      notification.success({
        message: NOTIFICATION_MESSAGES.GLOSSARY_TERM_DELETE_SUCCESS,
        placement: 'bottomLeft',
        duration: 3,
      });
    },
  });

  const handleDelete = useCallback(
    (termId: string, options?: { recursive?: boolean; hardDelete?: boolean }) => {
      return mutation.mutateAsync({
        termId,
        recursive: options?.recursive ?? true,
        hardDelete: options?.hardDelete ?? true,
      });
    },
    [mutation]
  );

  return {
    ...mutation,
    handleDelete,
  };
};
